import {
	getApiUrl,
	type OrderAttachmentResponseDto,
} from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useUploadOrderAttachmentsMutation } from "Order/Api/Mutation/uploadOrderAttachmentsMutation";
import { useOrderAttachmentsQuery } from "Order/Api/Query/useOrderAttachmentsQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { VisuallyHiddenInput } from "Shared/Components/Form/CustomFormComponent/FormUploadButton";
import { CloudUpload, PictureAsPdf } from "@mui/icons-material";
import {
	Box,
	Chip,
	Dialog,
	Grid2,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { type ChangeEvent, useState } from "react";
import { toast } from "sonner";

type OrderAttachmentsWidgetProps = {
	orderId: number;
};

// Mirrors app/core order-attachment.constants.ts; the server stays authoritative.
const MAX_ATTACHMENTS_PER_ORDER = 5;
const MAX_ATTACHMENT_MB = 10;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;
const ACCEPTED_ATTACHMENT_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"application/pdf",
];

const attachmentsResource = Resources.base.order.attachments;

// LocalStrategy returns a root-relative "/storage/…" path instead of a signed URL.
const resolveAttachmentUrl = (url: string) =>
	url.startsWith("http") ? url : `${getApiUrl()}${url}`;

const attachmentAlt = (attachment: OrderAttachmentResponseDto) =>
	attachment.originalFileName ?? `File ${attachment.id}`;

export function OrderAttachmentsWidget({
	orderId,
}: OrderAttachmentsWidgetProps) {
	const { t } = useResource();
	const { data: attachments = [], isLoading } =
		useOrderAttachmentsQuery(orderId);
	const { mutate: uploadAttachments, isPending } =
		useUploadOrderAttachmentsMutation(orderId);
	const canUpload = hasPermission(UserPermissions.OrderModule.update);
	const [lightboxAttachment, setLightboxAttachment] =
		useState<OrderAttachmentResponseDto | null>(null);

	const remaining = MAX_ATTACHMENTS_PER_ORDER - attachments.length;
	const uploadDisabled = isLoading || isPending || remaining <= 0;

	const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files ?? []);
		// Allow re-selecting the same file after a rejected or completed pick.
		event.target.value = "";
		// The label's `disabled` does not reach the nested native input.
		if (uploadDisabled) {
			return;
		}
		if (files.length === 0) {
			return;
		}
		if (files.length > remaining) {
			toast.warning(t(attachmentsResource.tooMany, { remaining }));
			return;
		}
		for (const file of files) {
			if (!ACCEPTED_ATTACHMENT_TYPES.includes(file.type)) {
				toast.warning(
					t(attachmentsResource.unsupportedType, { name: file.name }),
				);
				return;
			}
			if (file.size > MAX_ATTACHMENT_BYTES) {
				toast.warning(
					t(attachmentsResource.fileTooLarge, {
						name: file.name,
						maxMb: MAX_ATTACHMENT_MB,
					}),
				);
				return;
			}
		}
		uploadAttachments(files);
	};

	const uploadAction = canUpload ? (
		<Tooltip
			title={
				remaining <= 0
					? t(attachmentsResource.uploadDisabledFull, {
							max: MAX_ATTACHMENTS_PER_ORDER,
						})
					: t(attachmentsResource.upload)
			}
		>
			<span>
				<IconButton
					component="label"
					color="primary"
					disabled={uploadDisabled}
					aria-label={t(attachmentsResource.upload)}
				>
					<CloudUpload />
					<VisuallyHiddenInput
						type="file"
						multiple
						disabled={uploadDisabled}
						accept={ACCEPTED_ATTACHMENT_TYPES.join(",")}
						onChange={handleFilesPicked}
					/>
				</IconButton>
			</span>
		</Tooltip>
	) : undefined;

	return (
		<BaseCard
			title={t(attachmentsResource.title)}
			action={uploadAction}
			isLoading={isLoading || isPending}
		>
			{isLoading ? (
				<></>
			) : attachments.length === 0 ? (
				<Typography color="text.secondary">
					{t(attachmentsResource.empty)}
				</Typography>
			) : (
				<Grid2 container spacing={1}>
					{attachments.map((attachment) => (
						<Grid2
							key={attachment.id}
							size={{ xs: 6, sm: 4, md: 3 }}
							sx={{ position: "relative" }}
						>
							{attachment.mimeType === "application/pdf" ? (
								<Box
									component="a"
									href={resolveAttachmentUrl(attachment.url)}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										height: 120,
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										gap: 0.5,
										p: 1,
										borderRadius: 1,
										bgcolor: "action.hover",
										color: "text.secondary",
										textDecoration: "none",
									}}
								>
									<PictureAsPdf fontSize="large" />
									<Typography
										variant="caption"
										noWrap
										sx={{ maxWidth: "100%" }}
									>
										{attachmentAlt(attachment)}
									</Typography>
								</Box>
							) : (
								<Box
									component="img"
									src={resolveAttachmentUrl(attachment.url)}
									alt={attachmentAlt(attachment)}
									loading="lazy"
									onClick={() => setLightboxAttachment(attachment)}
									sx={{
										width: "100%",
										height: 120,
										objectFit: "cover",
										borderRadius: 1,
										cursor: "pointer",
									}}
								/>
							)}
							{attachment.source === "admin" && (
								<Chip
									label={t(attachmentsResource.adminBadge)}
									size="small"
									color="primary"
									sx={{ position: "absolute", top: 4, left: 4 }}
								/>
							)}
						</Grid2>
					))}
				</Grid2>
			)}
			<Dialog
				open={lightboxAttachment !== null}
				onClose={() => setLightboxAttachment(null)}
				maxWidth="lg"
				aria-label={t(attachmentsResource.lightbox)}
			>
				{lightboxAttachment ? (
					<Box
						component="img"
						src={resolveAttachmentUrl(lightboxAttachment.url)}
						alt={attachmentAlt(lightboxAttachment)}
						onClick={() => setLightboxAttachment(null)}
						sx={{ display: "block", maxWidth: "100%", maxHeight: "90vh" }}
					/>
				) : (
					<></>
				)}
			</Dialog>
		</BaseCard>
	);
}
